"use client";

import React, { useState } from "react";
import {
  Sparkles,
  Cpu,
  Layers,
  CheckCircle2,
  AlertTriangle,
  RefreshCw,
  Code2,
  Eye,
  ShieldCheck,
  Plane,
  TrendingUp,
  FileCheck2,
  Sliders,
  Palette,
} from "lucide-react";

type PatternType = "constrained" | "declarative" | "unconstrained";
type PresetId = "flight" | "analytics" | "approval";
type ThemeToken = "rose" | "violet" | "emerald";
type RadiusToken = "rounded-lg" | "rounded-2xl" | "rounded-none";

interface PresetData {
  id: PresetId;
  label: string;
  icon: React.ReactNode;
  toolName: string;
  intent: string;
  payload: Record<string, unknown>;
  a2uiGraph: Record<string, unknown>;
  unconstrainedRawHtml: string;
}

const PRESETS: Record<PresetId, PresetData> = {
  flight: {
    id: "flight",
    label: "Flugbuchung (Ticket)",
    icon: <Plane className="h-4 w-4" />,
    toolName: "renderFlightConfirmation",
    intent: "Bestätige den gebuchten Flug von MUC nach BER für morgen früh.",
    payload: {
      flightNumber: "LH 2034",
      departure: { airport: "MUC", city: "München", time: "07:15" },
      arrival: { airport: "BER", city: "Berlin", time: "08:25" },
      passenger: "Lukas K.",
      gate: "G14",
      status: "CONFIRMED",
      priceEuro: 189,
    },
    a2uiGraph: {
      surfaceId: "flight-ticket-surface",
      root: "ticket-card",
      components: [
        { type: "Card", id: "ticket-card", tokenVariant: "elevated" },
        { type: "Header", title: "Flugbestätigung", badge: "LH 2034" },
        { type: "RouteDisplay", from: "MUC", to: "BER", duration: "1h 10m" },
        { type: "MetaRow", gate: "G14", passenger: "Lukas K." },
        { type: "ActionButton", label: "In Wallet speichern", action: "saveWallet" },
      ],
    },
    unconstrainedRawHtml: `<div style="background:#eef;padding:12px;border:1px solid #99f;font-family:Arial"><h3>Flight MUC-BER LH2034</h3><p>Time: 07:15 - 08:25 | Lukas K. (G14)</p><button onclick="alert('save')">Save Ticket</button></div>`,
  },
  analytics: {
    id: "analytics",
    label: "KPI Dashboard Card",
    icon: <TrendingUp className="h-4 w-4" />,
    toolName: "renderMetricCard",
    intent: "Zeige mir den aktuellen Monatsumsatz im Vergleich zum Vormonat.",
    payload: {
      metric: "Monthly Recurring Revenue",
      value: "48.250 €",
      changePercent: "+14.2%",
      trend: "positive",
      timeframe: "Letzte 30 Tage",
      benchmark: "Ziel: 45.000 €",
    },
    a2uiGraph: {
      surfaceId: "metric-surface",
      root: "kpi-card",
      components: [
        { type: "KpiMetric", id: "kpi-card", tokenVariant: "accent" },
        { type: "MetricTitle", label: "Monthly Recurring Revenue" },
        { type: "MetricValue", value: "48.250 €", delta: "+14.2%" },
        { type: "ProgressBar", current: 48250, target: 45000 },
      ],
    },
    unconstrainedRawHtml: `<div style="background:#fff;border-radius:4px;box-shadow:0 0 5px #ccc;padding:15px"><h4 style="color:#666">MRR</h4><div style="font-size:24px;color:green">48.250 € (+14.2%)</div><small>Ziel erreicht!</small></div>`,
  },
  approval: {
    id: "approval",
    label: "Freigabe-Workflow",
    icon: <FileCheck2 className="h-4 w-4" />,
    toolName: "renderApprovalAction",
    intent: "Reiche die Spesenabrechnung zur Genehmigung ein.",
    payload: {
      claimId: "EXP-2026-981",
      submittedBy: "Sarah M. (Design Lead)",
      amount: "640,00 €",
      category: "Software-Lizenzen (Figma)",
      urgency: "HIGH",
      policyCheck: "100% Policy-konform",
    },
    a2uiGraph: {
      surfaceId: "approval-surface",
      root: "approval-box",
      components: [
        { type: "ApprovalBanner", id: "approval-box" },
        { type: "ClaimDetails", id: "EXP-2026-981", amount: "640,00 €" },
        { type: "ActionGroup", approve: "Freigeben", reject: "Rückfragen" },
      ],
    },
    unconstrainedRawHtml: `<div style="border:2px dashed red;padding:10px;margin-top:10px"><b>Freigabe: EXP-981</b><p>Betrag: 640 € (Sarah M.)</p><a href="#">Freigeben</a> | <a href="#">Ablehnen</a></div>`,
  },
};

export default function GenerativeUiPlayground() {
  const [pattern, setPattern] = useState<PatternType>("constrained");
  const [presetKey, setPresetKey] = useState<PresetId>("flight");
  const [themeToken, setThemeToken] = useState<ThemeToken>("rose");
  const [radiusToken, setRadiusToken] = useState<RadiusToken>("rounded-2xl");
  const [activeTab, setActiveTab] = useState<"render" | "payload" | "audit">("render");
  const [isSimulating, setIsSimulating] = useState(false);
  const [actionFeedback, setActionFeedback] = useState<string | null>(null);

  const currentPreset = PRESETS[presetKey];

  // Accent styling based on theme token
  const tokenBgColor =
    themeToken === "rose"
      ? "bg-rose-500 text-white"
      : themeToken === "violet"
        ? "bg-violet-600 text-white"
        : "bg-emerald-600 text-white";

  const tokenBorderColor =
    themeToken === "rose"
      ? "border-rose-300 dark:border-rose-900/60"
      : themeToken === "violet"
        ? "border-violet-300 dark:border-violet-900/60"
        : "border-emerald-300 dark:border-emerald-900/60";

  const tokenBadgeBg =
    themeToken === "rose"
      ? "bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20"
      : themeToken === "violet"
        ? "bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20"
        : "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20";

  const handleSimulate = () => {
    setIsSimulating(true);
    setActionFeedback(null);
    setTimeout(() => {
      setIsSimulating(false);
      setActionFeedback(
        pattern === "unconstrained"
          ? "Unconstrained Mode: Warnung vor Token-Drift und fehlenden ARIA-Attributen!"
          : "Validierung erfolgreich: 0 CLS, 100% W3C DTCG Token-Match."
      );
    }, 350);
  };

  return (
    <div className="flex flex-col gap-5 select-none min-h-[500px]">
      {/* Top Controls: Preset & Pattern Switcher */}
      <div className="flex flex-col gap-3 rounded-xl border border-neutral-200/80 bg-neutral-50/80 p-3 sm:p-4 dark:border-neutral-800 dark:bg-neutral-900/60">
        <div className="flex flex-wrap items-center justify-between gap-3">
          {/* Preset Buttons */}
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="mr-1 text-xs font-mono font-medium text-neutral-500 dark:text-neutral-400">
              Szenario:
            </span>
            {(Object.keys(PRESETS) as PresetId[]).map((key) => {
              const item = PRESETS[key];
              const isSelected = presetKey === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    setPresetKey(key);
                    setActionFeedback(null);
                  }}
                  className={`inline-flex min-h-[38px] items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-all cursor-pointer focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-rose-500 ${
                    isSelected
                      ? "bg-neutral-900 text-white shadow-xs dark:bg-white dark:text-neutral-900"
                      : "bg-white text-neutral-700 border border-neutral-200 hover:bg-neutral-100 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-750"
                  }`}
                  aria-pressed={isSelected}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>

          {/* Simulate Action Button */}
          <button
            type="button"
            onClick={handleSimulate}
            disabled={isSimulating}
            className="inline-flex min-h-[38px] items-center gap-1.5 rounded-lg bg-rose-600 px-3.5 py-1.5 text-xs font-semibold text-white shadow-xs hover:bg-rose-700 active:scale-95 transition-all cursor-pointer disabled:opacity-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-rose-500"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isSimulating ? "animate-spin" : ""}`} />
            <span>Neu validieren</span>
          </button>
        </div>

        {/* Pattern & Token Selection Bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-neutral-200/60 pt-3 dark:border-neutral-800/80">
          {/* Architecture Pattern Radio / Buttons */}
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="mr-1 text-xs font-mono font-medium text-neutral-500 dark:text-neutral-400">
              Muster:
            </span>
            <button
              type="button"
              onClick={() => setPattern("constrained")}
              className={`min-h-[36px] rounded-lg px-2.5 py-1 text-xs font-medium transition-all cursor-pointer ${
                pattern === "constrained"
                  ? "bg-rose-600 text-white font-semibold shadow-xs"
                  : "bg-neutral-200/70 text-neutral-700 hover:bg-neutral-200 dark:bg-neutral-800 dark:text-neutral-300"
              }`}
            >
              1. Constrained Tool Calling
            </button>
            <button
              type="button"
              onClick={() => setPattern("declarative")}
              className={`min-h-[36px] rounded-lg px-2.5 py-1 text-xs font-medium transition-all cursor-pointer ${
                pattern === "declarative"
                  ? "bg-purple-600 text-white font-semibold shadow-xs"
                  : "bg-neutral-200/70 text-neutral-700 hover:bg-neutral-200 dark:bg-neutral-800 dark:text-neutral-300"
              }`}
            >
              2. Declarative A2UI
            </button>
            <button
              type="button"
              onClick={() => setPattern("unconstrained")}
              className={`min-h-[36px] rounded-lg px-2.5 py-1 text-xs font-medium transition-all cursor-pointer ${
                pattern === "unconstrained"
                  ? "bg-amber-600 text-white font-semibold shadow-xs"
                  : "bg-neutral-200/70 text-neutral-700 hover:bg-neutral-200 dark:bg-neutral-800 dark:text-neutral-300"
              }`}
            >
              3. Unconstrained HTML (Drift)
            </button>
          </div>

          {/* Design Token Live Customizer */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <Palette className="h-3.5 w-3.5 text-neutral-400" />
              <div className="flex items-center gap-1">
                {(["rose", "violet", "emerald"] as ThemeToken[]).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setThemeToken(t)}
                    title={`Design Token Accent: ${t}`}
                    className={`h-5 w-5 rounded-full transition-transform cursor-pointer ${
                      t === "rose"
                        ? "bg-rose-500"
                        : t === "violet"
                          ? "bg-violet-600"
                          : "bg-emerald-500"
                    } ${themeToken === t ? "scale-125 ring-2 ring-neutral-900 dark:ring-white" : "opacity-60 hover:opacity-100"}`}
                  />
                ))}
              </div>
            </div>

            <div className="flex items-center gap-1">
              <Sliders className="h-3.5 w-3.5 text-neutral-400" />
              <select
                aria-label="Radius Design Token auswählen"
                value={radiusToken}
                onChange={(e) => setRadiusToken(e.target.value as RadiusToken)}
                className="h-7 rounded border border-neutral-200 bg-white px-1.5 text-xs font-mono text-neutral-700 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-300 cursor-pointer"
              >
                <option value="rounded-2xl">--radius: 1rem (2xl)</option>
                <option value="rounded-lg">--radius: 0.5rem (lg)</option>
                <option value="rounded-none">--radius: 0px (flat)</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Inline Feedback Banner */}
      {actionFeedback && (
        <div
          role="status"
          className={`flex items-center gap-2 rounded-xl p-3 text-xs font-mono ${
            pattern === "unconstrained"
              ? "border border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-300"
              : "border border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-300"
          }`}
        >
          {pattern === "unconstrained" ? (
            <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
          ) : (
            <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
          )}
          <span>{actionFeedback}</span>
        </div>
      )}

      {/* View Switcher Tabs */}
      <div className="flex items-center justify-between border-b border-neutral-200 dark:border-neutral-800 pb-2">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setActiveTab("render")}
            className={`inline-flex min-h-[36px] items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all cursor-pointer ${
              activeTab === "render"
                ? "bg-neutral-100 text-neutral-900 dark:bg-neutral-800 dark:text-white"
                : "text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white"
            }`}
          >
            <Eye className="h-3.5 w-3.5" />
            <span>Gerenderte UI-Fläche</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("payload")}
            className={`inline-flex min-h-[36px] items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all cursor-pointer ${
              activeTab === "payload"
                ? "bg-neutral-100 text-neutral-900 dark:bg-neutral-800 dark:text-white"
                : "text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white"
            }`}
          >
            <Code2 className="h-3.5 w-3.5" />
            <span>LLM Payload (JSON/A2UI)</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("audit")}
            className={`inline-flex min-h-[36px] items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all cursor-pointer ${
              activeTab === "audit"
                ? "bg-neutral-100 text-neutral-900 dark:bg-neutral-800 dark:text-white"
                : "text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white"
            }`}
          >
            <ShieldCheck className="h-3.5 w-3.5" />
            <span>Architektur-Audit</span>
          </button>
        </div>

        {/* Live Intent Bubble */}
        <div className="hidden sm:flex items-center gap-1.5 font-mono text-[11px] text-neutral-500 dark:text-neutral-400">
          <span className="font-semibold text-neutral-700 dark:text-neutral-300">Prompt:</span>
          <span className="truncate max-w-[280px] italic">„{currentPreset.intent}“</span>
        </div>
      </div>

      {/* Main Tab Content Area (min-h-[320px] for stable CLS) */}
      <div className="min-h-[320px] flex flex-col justify-center">
        {/* Tab 1: Render View */}
        {activeTab === "render" && (
          <div className="w-full flex items-center justify-center p-2 sm:p-6 bg-neutral-100/60 dark:bg-neutral-950/40 rounded-2xl border border-neutral-200/60 dark:border-neutral-800/80">
            {pattern === "unconstrained" ? (
              /* Unconstrained HTML mode: simulate raw injection drift */
              <div className="w-full max-w-md rounded-lg border-2 border-dashed border-amber-400 bg-amber-50/50 p-4 dark:bg-amber-950/20">
                <div className="mb-2 flex items-center justify-between text-[11px] font-mono text-amber-700 dark:text-amber-400 font-bold">
                  <span>UNCONSTRAINED HTML INJECTION</span>
                  <span className="text-red-500">CLS RISK: +0.380</span>
                </div>
                <div
                  className="rounded bg-white p-3 shadow-xs dark:bg-neutral-900"
                  dangerouslySetInnerHTML={{ __html: currentPreset.unconstrainedRawHtml }}
                />
                <p className="mt-2 text-[10px] text-amber-600 dark:text-amber-400 font-mono">
                  ⚠️ Warnung: Farbkontrast nicht geprüft, kein Design-Token-Contract, fehlende ARIA-Attribute.
                </p>
              </div>
            ) : presetKey === "flight" ? (
              /* Flight Ticket Component rendered with Tokens */
              <div
                className={`w-full max-w-md border ${tokenBorderColor} bg-white dark:bg-neutral-900 shadow-md p-5 sm:p-6 ${radiusToken} transition-all`}
              >
                <div className="flex items-center justify-between border-b border-neutral-100 dark:border-neutral-800 pb-3">
                  <div className="flex items-center gap-2">
                    <Plane className="h-4 w-4 text-neutral-500" />
                    <span className="font-heading text-sm font-bold text-neutral-900 dark:text-white">
                      Boarding Pass
                    </span>
                  </div>
                  <span className={`rounded-full px-2 py-0.5 font-mono text-[10px] font-bold ${tokenBadgeBg}`}>
                    {(currentPreset.payload.flightNumber as string) || "LH 2034"}
                  </span>
                </div>

                <div className="my-4 flex items-center justify-between px-2">
                  <div className="text-center">
                    <span className="block font-mono text-2xl font-black text-neutral-900 dark:text-white">
                      MUC
                    </span>
                    <span className="text-xs text-neutral-500">München · 07:15</span>
                  </div>
                  <div className="flex flex-col items-center gap-1">
                    <span className="font-mono text-[10px] text-neutral-400">1h 10m direct</span>
                    <div className="h-0.5 w-16 bg-neutral-200 dark:bg-neutral-700 relative">
                      <div className="absolute -top-1 right-0 h-2 w-2 rounded-full bg-rose-500" />
                    </div>
                  </div>
                  <div className="text-center">
                    <span className="block font-mono text-2xl font-black text-neutral-900 dark:text-white">
                      BER
                    </span>
                    <span className="text-xs text-neutral-500">Berlin · 08:25</span>
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between border-t border-neutral-100 dark:border-neutral-800 pt-3 text-xs">
                  <div>
                    <span className="block font-mono text-[10px] text-neutral-400">Passagier</span>
                    <span className="font-semibold text-neutral-800 dark:text-neutral-200">
                      Lukas K.
                    </span>
                  </div>
                  <div>
                    <span className="block font-mono text-[10px] text-neutral-400">Gate</span>
                    <span className="font-semibold text-neutral-800 dark:text-neutral-200">G14</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setActionFeedback("Aktion 'In Wallet speichern' ausgelöst.")}
                    className={`min-h-[36px] rounded-lg px-3 py-1.5 text-xs font-semibold ${tokenBgColor} transition-transform active:scale-95 cursor-pointer`}
                  >
                    Wallet
                  </button>
                </div>
              </div>
            ) : presetKey === "analytics" ? (
              /* Analytics Card rendered with Tokens */
              <div
                className={`w-full max-w-md border ${tokenBorderColor} bg-white dark:bg-neutral-900 shadow-md p-5 sm:p-6 ${radiusToken} transition-all`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs font-medium text-neutral-500 dark:text-neutral-400">
                    Monthly Recurring Revenue
                  </span>
                  <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 font-mono text-[10px] font-bold text-emerald-600 dark:text-emerald-400">
                    +14.2% vs. Vormonat
                  </span>
                </div>
                <div className="my-3">
                  <span className="font-heading text-3xl font-black tracking-tight text-neutral-900 dark:text-white">
                    48.250 €
                  </span>
                  <span className="ml-2 font-mono text-xs text-neutral-400">Ziel: 45.000 €</span>
                </div>
                {/* Progress bar */}
                <div className="h-2 w-full overflow-hidden rounded-full bg-neutral-100 dark:bg-neutral-800">
                  <div className={`h-full w-[88%] ${tokenBgColor}`} />
                </div>
                <div className="mt-3 flex items-center justify-between text-xs text-neutral-500">
                  <span>Letzte 30 Tage</span>
                  <button
                    type="button"
                    onClick={() => setActionFeedback("Detail-Report heruntergeladen.")}
                    className="font-semibold hover:underline cursor-pointer"
                  >
                    Export CSV
                  </button>
                </div>
              </div>
            ) : (
              /* Approval Workflow Dialog rendered with Tokens */
              <div
                className={`w-full max-w-md border ${tokenBorderColor} bg-white dark:bg-neutral-900 shadow-md p-5 sm:p-6 ${radiusToken} transition-all`}
              >
                <div className="flex items-center justify-between border-b border-neutral-100 dark:border-neutral-800 pb-3">
                  <span className="font-mono text-xs font-bold text-neutral-900 dark:text-white">
                    Spesenfreigabe EXP-2026-981
                  </span>
                  <span className="rounded-full bg-amber-500/10 px-2 py-0.5 font-mono text-[10px] font-bold text-amber-600 dark:text-amber-400">
                    PRIO: HOCH
                  </span>
                </div>
                <div className="my-4 space-y-1.5 text-xs text-neutral-600 dark:text-neutral-300">
                  <p>
                    <span className="font-semibold">Eingereicht von:</span> Sarah M. (Design Lead)
                  </p>
                  <p>
                    <span className="font-semibold">Betrag:</span> 640,00 € (Figma Lizenzen)
                  </p>
                  <p className="flex items-center gap-1 font-mono text-emerald-600 dark:text-emerald-400">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    <span>Automatische Prüfung: 100% Policy-konform</span>
                  </p>
                </div>
                <div className="flex items-center gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setActionFeedback("Spesenabrechnung genehmigt!")}
                    className={`flex-1 min-h-[38px] rounded-lg px-3 py-1.5 text-xs font-semibold ${tokenBgColor} cursor-pointer transition-transform active:scale-95`}
                  >
                    Freigeben
                  </button>
                  <button
                    type="button"
                    onClick={() => setActionFeedback("Rückfrage an Antragsteller versandt.")}
                    className="min-h-[38px] rounded-lg border border-neutral-200 bg-neutral-100 px-3 py-1.5 text-xs font-medium text-neutral-700 hover:bg-neutral-200 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-300 cursor-pointer"
                  >
                    Rückfragen
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tab 2: Payload View */}
        {activeTab === "payload" && (
          <div className="w-full rounded-2xl border border-neutral-200 bg-neutral-900 p-4 font-mono text-xs text-neutral-200 dark:border-neutral-800 overflow-x-auto">
            <div className="mb-2 flex items-center justify-between text-[11px] text-neutral-400 border-b border-neutral-800 pb-2">
              <span>
                Format: {pattern === "declarative" ? "A2UI Component Tree (JSONL)" : "Tool Call Arguments (Zod)"}
              </span>
              <span className="text-emerald-400 font-semibold">Schema: OK</span>
            </div>
            <pre className="text-[11px] leading-relaxed">
              {pattern === "declarative"
                ? JSON.stringify(currentPreset.a2uiGraph, null, 2)
                : JSON.stringify(currentPreset.payload, null, 2)}
            </pre>
          </div>
        )}

        {/* Tab 3: Architecture Audit Scorecard */}
        {activeTab === "audit" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-1">
            <div className="rounded-xl border border-neutral-200 bg-white p-4 shadow-2xs dark:border-neutral-800 dark:bg-neutral-900">
              <span className="font-mono text-xs font-semibold text-neutral-500">Layout-Shift (CLS)</span>
              <div className="mt-1 flex items-baseline gap-2">
                <span
                  className={`text-2xl font-bold font-mono ${
                    pattern === "unconstrained" ? "text-amber-500" : "text-emerald-500"
                  }`}
                >
                  {pattern === "unconstrained" ? "0.380 (FAIL)" : "0.000 (PASS)"}
                </span>
              </div>
              <p className="mt-2 text-xs text-neutral-500">
                {pattern === "unconstrained"
                  ? "Unberechenbare DOM-Höhe durch dynamisches HTML-Streaming führt zu störenden Rucklern."
                  : "Feste Container-Dimensionen und deterministische Komponentenschemata garantieren CLS = 0."}
              </p>
            </div>

            <div className="rounded-xl border border-neutral-200 bg-white p-4 shadow-2xs dark:border-neutral-800 dark:bg-neutral-900">
              <span className="font-mono text-xs font-semibold text-neutral-500">Design Token Contract</span>
              <div className="mt-1 flex items-baseline gap-2">
                <span
                  className={`text-2xl font-bold font-mono ${
                    pattern === "unconstrained" ? "text-amber-500" : "text-emerald-500"
                  }`}
                >
                  {pattern === "unconstrained" ? "32% Drift" : "100% W3C DTCG"}
                </span>
              </div>
              <p className="mt-2 text-xs text-neutral-500">
                {pattern === "unconstrained"
                  ? "Inkonsistente Hex-Farben und abweichende Radien brechen die Markenrichtlinien."
                  : "Frontend-Mapping bindet alle Props streng an bestehende CSS-Variablen & Design Tokens."}
              </p>
            </div>

            <div className="rounded-xl border border-neutral-200 bg-white p-4 shadow-2xs dark:border-neutral-800 dark:bg-neutral-900">
              <span className="font-mono text-xs font-semibold text-neutral-500">Accessibility (a11y)</span>
              <div className="mt-1 flex items-baseline gap-2">
                <span
                  className={`text-2xl font-bold font-mono ${
                    pattern === "unconstrained" ? "text-rose-500" : "text-emerald-500"
                  }`}
                >
                  {pattern === "unconstrained" ? "Ungeschützt" : "WCAG 2.2 AAA"}
                </span>
              </div>
              <p className="mt-2 text-xs text-neutral-500">
                {pattern === "unconstrained"
                  ? "Rohes HTML verzichtet auf Tastaturfokus, ARIA-Live-Regions und screenreader-optimierte Labels."
                  : "Headless Primitives (Radix/Aria) sichern native Barrierefreiheit und Fokus-Management."}
              </p>
            </div>

            <div className="rounded-xl border border-neutral-200 bg-white p-4 shadow-2xs dark:border-neutral-800 dark:bg-neutral-900">
              <span className="font-mono text-xs font-semibold text-neutral-500">Sicherheitsarchitektur</span>
              <div className="mt-1 flex items-baseline gap-2">
                <span
                  className={`text-2xl font-bold font-mono ${
                    pattern === "unconstrained" ? "text-rose-500" : "text-emerald-500"
                  }`}
                >
                  {pattern === "unconstrained" ? "XSS-Risiko" : "100% Type-Safe"}
                </span>
              </div>
              <p className="mt-2 text-xs text-neutral-500">
                {pattern === "unconstrained"
                  ? "dangerouslySetInnerHTML öffnet Angriffsvektoren bei prompt-injizierten Inhalten."
                  : "Zod-Validierung prüft alle Argumente vor dem Rendern; kein Code wird zur Laufzeit evaluiert."}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
